import React, { useEffect, useState } from "react";
import { PhoneCall, Mail, Clock, Wrench, GraduationCap, Headphones, UserCog } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, EmptyState, SectionLabel } from "@/components/Common";

const TYPE_ICON = {
  "Functional Support": Headphones,
  "Technical Support":  Wrench,
  "Training Support":   GraduationCap,
};

export default function Support() {
  const [items, setItems] = useState([]);
  const [programs, setPrograms] = useState([]);

  useEffect(() => {
    api.get("/escalations").then((r) => setItems(r.data)).catch(() => {});
    api.get("/programs").then((r) => setPrograms(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      <PageHeader
        eyebrow="The Directory"
        title="Support"
        subtitle="Know exactly who to contact for every program you work on, and the expected response time."
        testid="support-header" />

      {programs.length === 0 && (
        <EmptyState icon={PhoneCall} title="No programs assigned" hint="An administrator needs to grant you program access first." />
      )}

      <div className="space-y-12" data-testid="support-programs">
        {programs.map((program) => {
          const contacts = items.filter((e) => e.program_id === program.id);
          return (
            <section key={program.id} data-testid={`support-program-${program.id}`}>
              <SectionLabel>{program.name}</SectionLabel>

              <div className="rounded-2xl border border-primary/25 bg-secondary/30 p-5 mb-5 flex items-center gap-4 flex-wrap" data-testid={`pm-contact-${program.id}`}>
                <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground font-semibold flex items-center justify-center shrink-0">
                  {program.pm_name?.split(" ").map((s) => s[0]).slice(0, 2).join("")}
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-primary">Program Manager</p>
                  <p className="font-display text-lg text-foreground">{program.pm_name}</p>
                </div>
                <a href={`mailto:${program.pm_email}`} className="ml-auto text-sm text-primary kp-link flex items-center gap-1.5 font-semibold">
                  <Mail className="w-4 h-4" /> {program.pm_email}
                </a>
              </div>

              {contacts.length === 0 ? (
                <p className="text-sm text-muted-foreground italic pl-1">No additional support contacts configured for this program.</p>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 stagger" data-testid={`support-grid-${program.id}`}>
                  {contacts.map((e) => {
                    const Icon = TYPE_ICON[e.type] || UserCog;
                    return (
                      <div key={e.id} className="kp-card kp-card-lift p-5">
                        <div className="flex items-center gap-2.5 mb-3">
                          <div className="w-10 h-10 rounded-xl bg-secondary/60 flex items-center justify-center">
                            <Icon className="w-4 h-4 text-primary" />
                          </div>
                          <span className="text-sm font-semibold text-foreground">{e.type}</span>
                        </div>
                        <p className="text-sm text-foreground/80">{e.name}</p>
                        <a href={`mailto:${e.email}`} className="text-sm text-primary kp-link break-all font-medium">{e.email}</a>
                        <div className="mt-4 pt-3 border-t border-border flex items-center justify-between gap-2">
                          <span className="text-xs text-muted-foreground truncate">{e.note}</span>
                          <span className="text-[10px] font-mono uppercase tracking-wider rounded-full bg-muted px-2 py-0.5 text-muted-foreground flex items-center gap-1 shrink-0">
                            <Clock className="w-3 h-3" /> {e.sla}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}
